import { useState } from 'react'
import { MousePointerClick, TrendingUp, Layers, CalendarDays } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useSequenceClickStats } from '@/api/hooks/useSequences'
import { useLanguage } from '@/i18n/LanguageContext'

/** YYYY-MM-DD de hoje, no fuso local (não UTC — senão o "hoje" vira ontem de madrugada) */
function hojeISO(): string {
  const d = new Date()
  const off = d.getTimezoneOffset() * 60000
  return new Date(d.getTime() - off).toISOString().slice(0, 10)
}

function diasAtrasISO(dias: number): string {
  const d = new Date()
  d.setDate(d.getDate() - dias)
  const off = d.getTimezoneOffset() * 60000
  return new Date(d.getTime() - off).toISOString().slice(0, 10)
}

export function ClickStatsPanel({ platform }: { platform?: 'ig' | 'fb' } = {}) {
  const { t } = useLanguage()
  const [from, setFrom] = useState<string>(hojeISO())
  const [to, setTo] = useState<string>(hojeISO())

  const { data, isLoading } = useSequenceClickStats(from, to, platform)

  const atalho = (dias: number) => {
    setFrom(dias === 0 ? hojeISO() : diasAtrasISO(dias))
    setTo(hojeISO())
  }

  const porEtapa = data?.porEtapa ?? []
  const maxEtapa = Math.max(1, ...porEtapa.map((e) => e.cliques))
  const etapasComClique = porEtapa.filter((e) => e.cliques > 0).length
  const temEnvios = porEtapa.some((e) => (e.envios ?? 0) > 0)

  return (
    <Card className="border-border/40">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <MousePointerClick className="h-4 w-4 text-primary" />
              {t('Cliques nas sequencias')}
            </CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              {t('Quem clicou no botao de cada etapa. Atualiza a cada 1 min.')}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => atalho(0)}>
              {t('Hoje')}
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => atalho(6)}>
              {t('7 dias')}
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => atalho(29)}>
              {t('30 dias')}
            </Button>
            <Input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="h-7 w-[9.5rem] text-xs"
            />
            <span className="text-xs text-muted-foreground">{t('ate')}</span>
            <Input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="h-7 w-[9.5rem] text-xs"
            />
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        {isLoading ? (
          <div className="grid gap-3 sm:grid-cols-3">
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
          </div>
        ) : (
          <>
            {/* resumo */}
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-lg border border-border/40 bg-muted/10 p-4">
                <div className="text-2xl font-bold">{data?.total ?? 0}</div>
                <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                  <TrendingUp className="h-3 w-3" /> {t('Cliques no periodo')}
                </div>
              </div>
              <div className="rounded-lg border border-border/40 bg-muted/10 p-4">
                <div className="text-2xl font-bold">{etapasComClique}</div>
                <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Layers className="h-3 w-3" /> {t('Etapas com clique')}
                </div>
              </div>
              <div className="rounded-lg border border-border/40 bg-muted/10 p-4">
                <div className="text-2xl font-bold">{data?.totalPaginas ?? 0}</div>
                <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                  <CalendarDays className="h-3 w-3" /> {t('Paginas com clique')}
                </div>
              </div>
            </div>

            {(data?.total ?? 0) === 0 && !temEnvios ? (
              <div className="rounded-lg border border-dashed border-border/40 p-6 text-center text-sm text-muted-foreground">
                {t('Nenhum clique no periodo selecionado.')}
              </div>
            ) : (
              <div className="grid gap-5 lg:grid-cols-2">
                {/* por etapa — onde o funil converte */}
                <div>
                  <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('Por etapa')}
                  </h4>
                  <div className="space-y-1.5">
                    {porEtapa.map((e) => (
                      <div key={e.etapa} className="flex items-center gap-2">
                        <span className="w-16 shrink-0 text-xs text-muted-foreground">
                          {t('Etapa')} {e.etapa}
                        </span>
                        <div className="h-5 flex-1 overflow-hidden rounded bg-muted/20">
                          <div
                            className="h-full rounded bg-primary/70 transition-all"
                            style={{ width: `${Math.max(4, (e.cliques / maxEtapa) * 100)}%` }}
                          />
                        </div>
                        <span className="w-10 shrink-0 text-right text-xs font-medium tabular-nums">
                          {e.cliques}
                        </span>
                        <span className="w-24 shrink-0 text-right text-[11px] text-muted-foreground tabular-nums">
                          {(e.envios ?? 0) > 0
                            ? `${e.envios} ${t('env.')} · ${e.ctr ?? 0}%`
                            : t('sem envios')}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* por página */}
                <div>
                  <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {t('Paginas que mais converteram')}
                  </h4>
                  <div className="space-y-1">
                    {(data?.porPagina ?? []).slice(0, 8).map((p, i) => (
                      <div
                        key={`${p.pagina}-${i}`}
                        className="flex items-center justify-between rounded border border-border/30 px-2.5 py-1.5"
                      >
                        <span className="truncate text-xs">{p.pagina}</span>
                        <span className="ml-2 shrink-0 text-xs font-medium tabular-nums">
                          {p.cliques}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
